'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo, useCallback, Suspense } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Index, Configure, useInstantSearch, useSearchBox, SearchBox } from 'react-instantsearch';
import { ALGOLIA_INDEX_NAME, SORT_BY_OPTIONS, NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import ProductListingPageName from 'components/consumer/ProductListingPage/components/ProductListingPageName';
import DesktopShowHideFiltersButton from 'components/consumer/ProductListingPage/components/desktop/DesktopShowHideFiltersButton';
import MobileGridColumnToggle from 'components/consumer/ProductListingPage/components/mobile/MobileGridColumnToggle';
import DesktopProductListingSidebarContainer from 'components/consumer/ProductListingPage/components/desktop/DesktopProductListingSidebarContainer';
import useScreen from 'hooks/useScreen';
import useAdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchContext';
import AdvancedSearchProductHits from 'components/consumer/consumer-search/advanced-search-next/components/AdvancedSearchProductHits';
import DesktopProductListingColorFilter from 'components/consumer/consumer-search/advanced-search-next/components/AlgoliaHits/components/DesktopProductListingColorFilter';
import ProductListingSortBy from 'components/consumer/consumer-search/advanced-search-next/components/AlgoliaHits/components/ProductListingSortBy';
import ProductListingPageWidgets from 'components/consumer/ProductListingPageWidgets';
import ProductListingPageDescriptionShell from 'components/consumer/consumer-search/advanced-search-next/components/ProductListingPageDescriptionUI';
import CategoryAlgoliaMobileFilters from './CategoryAlgoliaMobileFilters';
import useCategoryAlgoliaAnalytics from '../hooks/useCategoryAlgoliaAnalytics';
import CategoryAlgoliaSidebar from './CategoryAlgoliaSidebar';
import CategoryAlgoliaSEO from './CategoryAlgoliaSEO';
import CategoryAlgoliaJSONLD from './CategoryAlgoliaJSONLD';
import VirtualSearchBox from './VirtualSearchBox';
const CategoryAlgoliaLayout = t0 => {
  const $ = _c(86);
  const {
    slug,
    serverCategoryData,
    configureFilters,
    selectedFacets,
    onToggleFacet,
    onClearFacets
  } = t0;
  const {
    isMobile
  } = useScreen();
  const {
    currentProductIndex,
    minWidth,
    maxWidth
  } = useAdvancedSearchContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      skipSuspense: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    results,
    status
  } = useInstantSearch(t1);
  const {
    query: parentQuery
  } = useSearchBox();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [enrichedProducts, setEnrichedProducts] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsSidebarOpen(_temp);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const toggleSidebar = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = products => {
      setEnrichedProducts(products);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handleProductsChange = t4;
  const categoryData = serverCategoryData?.getCategoryPage;
  const pageName = categoryData?.pageName || slug;
  const t5 = categoryData?.pageId;
  let t6;
  if ($[4] !== enrichedProducts || $[5] !== pageName || $[6] !== t5) {
    t6 = {
      pageId: t5,
      pageName,
      products: enrichedProducts
    };
    $[4] = enrichedProducts;
    $[5] = pageName;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  useCategoryAlgoliaAnalytics(t6);
  const productsCount = results?.nbHits || 0;
  const hasProductsLoaded = status === "idle" || status === "error";
  const resultsCount = hasProductsLoaded && productsCount > 0 ? productsCount : null;
  const resultsCountText = resultsCount != null ? `${resultsCount} Products` : null;
  let filters;
  if ($[8] !== maxWidth || $[9] !== minWidth) {
    filters = ["item_availability=0", "visibility>1", "visibility<4"];
    const minNum = parseInt(minWidth, 10);
    const maxNum = parseInt(maxWidth, 10);
    if (!Number.isNaN(minNum) && minNum >= 1) {
      filters.push(`item_width>=${minNum}`);
    }
    if (!Number.isNaN(maxNum) && maxNum <= 300) {
      filters.push(`item_width<=${maxNum}`);
    }
    $[8] = maxWidth;
    $[9] = minWidth;
    $[10] = filters;
  } else {
    filters = $[10];
  }
  const secondaryNumericFilters = filters;
  const isProductsIndex = currentProductIndex === ALGOLIA_INDEX_NAME.PRODUCTS;
  let t7;
  if ($[11] !== categoryData) {
    t7 = <CategoryAlgoliaSEO categoryData={categoryData} />;
    $[11] = categoryData;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = categoryData?.pageLongName || pageName;
  const t9 = categoryData?.pageLongDescription || "";
  const t10 = categoryData?.pageId;
  const t11 = results?.nbHits;
  let t12;
  if ($[13] !== currentProductIndex || $[14] !== enrichedProducts || $[15] !== hasProductsLoaded || $[16] !== t10 || $[17] !== t11 || $[18] !== t8 || $[19] !== t9) {
    t12 = <CategoryAlgoliaJSONLD products={enrichedProducts} pageName={t8} pageDescription={t9} pageId={t10} totalHits={t11} currentProductIndex={currentProductIndex} hasProductsLoaded={hasProductsLoaded} />;
    $[13] = currentProductIndex;
    $[14] = enrichedProducts;
    $[15] = hasProductsLoaded;
    $[16] = t10;
    $[17] = t11;
    $[18] = t8;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = classNames("px-3 py-4 space-y-1", "md:px-0 md:py-0 md:space-y-0", "md:items-center md:bg-white md:flex md:h-[53px] md:sticky md:z-[11] md:gap-5 md:justify-between", "md:top-[117px]");
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== pageName) {
    t14 = <ProductListingPageName pageName={pageName} showPageNameWhileLoading={true} />;
    $[22] = pageName;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== resultsCountText) {
    t15 = resultsCountText && <span className="text-sm text-gray-light2 font-normal">{resultsCountText}</span>;
    $[24] = resultsCountText;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <DesktopShowHideFiltersButton onClick={toggleSidebar} />;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t14 || $[28] !== t15) {
    t17 = <div className="md:flex md:gap-5 md:items-center">{t14}{t15}{t16}</div>;
    $[27] = t14;
    $[28] = t15;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = classNames("flex flex-row justify-between gap-3 py-3", "md:block md:gap-0 md:py-0");
    t19 = classNames("flex-1 flex gap-1", "md:flex-initial md:block md:gap-0");
    $[30] = t18;
    $[31] = t19;
  } else {
    t18 = $[30];
    t19 = $[31];
  }
  let t20;
  if ($[32] !== categoryData?.filters || $[33] !== isMobile || $[34] !== onToggleFacet || $[35] !== productsCount || $[36] !== selectedFacets) {
    t20 = isMobile && <><CategoryAlgoliaMobileFilters filters={categoryData?.filters} selectedFacets={selectedFacets} onToggleFacet={onToggleFacet} productsCount={productsCount} /><div className="flex-1 min-w-[140px] flex items-center"><ProductListingSortBy sortByOptions={SORT_BY_OPTIONS.filter(_temp2)} /></div></>;
    $[32] = categoryData?.filters;
    $[33] = isMobile;
    $[34] = onToggleFacet;
    $[35] = productsCount;
    $[36] = selectedFacets;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== isMobile) {
    t21 = !isMobile && <div className="flex-1 md:flex-initial md:flex md:flex-row md:gap-4 md:items-center"><DesktopProductListingColorFilter /><ProductListingSortBy sortByOptions={SORT_BY_OPTIONS.filter(_temp3)} /></div>;
    $[38] = isMobile;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t20 || $[41] !== t21) {
    t22 = <div className={t19}>{t20}{t21}</div>;
    $[40] = t20;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <MobileGridColumnToggle />;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t22) {
    t24 = <div className={t18}>{t22}{t23}</div>;
    $[44] = t22;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t17 || $[47] !== t24) {
    t25 = <div className={t13}>{t17}{t24}</div>;
    $[46] = t17;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  const t26 = categoryData?.filters;
  let t27;
  if ($[49] !== onClearFacets || $[50] !== onToggleFacet || $[51] !== pageName || $[52] !== selectedFacets || $[53] !== t26) {
    t27 = <CategoryAlgoliaSidebar pageName={pageName} filters={t26} selectedFacets={selectedFacets} onToggleFacet={onToggleFacet} onClearFacets={onClearFacets} />;
    $[49] = onClearFacets;
    $[50] = onToggleFacet;
    $[51] = pageName;
    $[52] = selectedFacets;
    $[53] = t26;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== isSidebarOpen || $[56] !== t27) {
    t28 = <div className="hidden md:block"><DesktopProductListingSidebarContainer isOpen={isSidebarOpen}>{t27}</DesktopProductListingSidebarContainer></div>;
    $[55] = isSidebarOpen;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== isProductsIndex) {
    t29 = isProductsIndex && <AdvancedSearchProductHits onProductsChange={handleProductsChange} />;
    $[58] = isProductsIndex;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== configureFilters || $[61] !== currentProductIndex || $[62] !== isProductsIndex || $[63] !== parentQuery || $[64] !== secondaryNumericFilters) {
    t30 = !isProductsIndex && <Index indexName={currentProductIndex}><SearchBox classNames={{
        root: "hidden"
      }} /><VirtualSearchBox parentQuery={parentQuery} /><Configure facets={["*"]} filters={configureFilters} numericFilters={secondaryNumericFilters} hitsPerPage={NUMBER_OF_PRODUCTS_TO_SHOW} attributesToRetrieve={["_highlightResult", "product_id", "name", "is_in_stock"]} clickAnalytics={true} /><AdvancedSearchProductHits onProductsChange={handleProductsChange} /></Index>;
    $[60] = configureFilters;
    $[61] = currentProductIndex;
    $[62] = isProductsIndex;
    $[63] = parentQuery;
    $[64] = secondaryNumericFilters;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  const t31 = categoryData?.pageLongDescription || "";
  let t32;
  if ($[66] !== pageName || $[67] !== t31) {
    t32 = <ProductListingPageDescriptionShell pageLongDescription={t31} pageName={pageName} />;
    $[66] = pageName;
    $[67] = t31;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== t29 || $[70] !== t30 || $[71] !== t32) {
    t33 = <div className="md:space-y-8 md:mb-8 md:flex-1">{t29}{t30}{t32}</div>;
    $[69] = t29;
    $[70] = t30;
    $[71] = t32;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== t28 || $[74] !== t33) {
    t34 = <div className="md:flex md:flex-row md:gap md:mt-2">{t28}{t33}</div>;
    $[73] = t28;
    $[74] = t33;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  const t35 = categoryData?.pageId;
  let t36;
  if ($[76] !== t35) {
    t36 = <Suspense fallback={null}><ProductListingPageWidgets categoryId={t35} /></Suspense>;
    $[76] = t35;
    $[77] = t36;
  } else {
    t36 = $[77];
  }
  let t37;
  if ($[78] !== t25 || $[79] !== t34 || $[80] !== t36) {
    t37 = <FixedWidthContainer wide={true} noMobilePadding={true}>{t25}{t34}{t36}</FixedWidthContainer>;
    $[78] = t25;
    $[79] = t34;
    $[80] = t36;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== t12 || $[83] !== t37 || $[84] !== t7) {
    t38 = <>{t7}{t12}{t37}</>;
    $[82] = t12;
    $[83] = t37;
    $[84] = t7;
    $[85] = t38;
  } else {
    t38 = $[85];
  }
  return t38;
};
CategoryAlgoliaLayout.propTypes = {
  slug: PropTypes.string.isRequired,
  serverCategoryData: PropTypes.object,
  configureFilters: PropTypes.string,
  selectedFacets: PropTypes.object,
  onToggleFacet: PropTypes.func,
  onClearFacets: PropTypes.func
};
export default CategoryAlgoliaLayout;
function _temp(prev) {
  return !prev;
}
function _temp2(option) {
  return option.value !== "newest";
}
function _temp3(option_0) {
  return option_0.value !== "newest";
}